import { useState, useEffect, useRef } from 'react';
import { standaloneCompaniesAPI } from '../lib/api';
import Spinner from './ui/Spinner';

interface PDFProgressTrackerProps {
  uploadId: string;
  filename: string;
  onComplete?: (companyId: string, reportId: string) => void;
  onError?: (error: string) => void;
}

interface PDFUploadJob {
  uploadId: string;
  companyName: string;
  fiscalYear: string;
  filename: string;
  fileSize: number;
  status: 'pending' | 'processing' | 'completed' | 'failed';
  currentPhase: 'initializing' | 'preprocessing' | 'extracting' | 'validating' | 'refining' | 'completed';
  progressMessage: string;
  progressPercentage: number | null;
  error?: string;
  companyId?: string;
  reportId?: string;
}

const PHASE_LABELS: Record<string, string> = {
  initializing: 'Initializing',
  preprocessing: 'Preprocessing PDF',
  extracting: 'Extracting Financial Data',
  validating: 'Validating Results',
  refining: 'Refining Extraction',
  completed: 'Completed'
};

const PHASE_PROGRESS: Record<string, number> = {
  initializing: 5,
  preprocessing: 20,
  extracting: 50,
  validating: 80,
  refining: 90,
  completed: 100
};

// Define all phases in order
const ALL_PHASES = [
  'initializing',
  'preprocessing',
  'extracting',
  'validating',
  'refining',
  'completed'
] as const;

type PhaseStatus = 'pending' | 'in-progress' | 'completed' | 'failed';

export default function PDFProgressTracker({
  uploadId,
  filename,
  onComplete,
  onError
}: PDFProgressTrackerProps) {
  const [job, setJob] = useState<PDFUploadJob | null>(null);
  const [error, setError] = useState<string | null>(null);
  const pollingIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const hasCompletedRef = useRef(false);

  useEffect(() => {
    if (!uploadId) return;

    const pollStatus = async () => {
      try {
        const jobData = await standaloneCompaniesAPI.getPDFUploadStatus(uploadId);
        setJob(jobData);

        // Handle completion
        if (jobData.status === 'completed' && !hasCompletedRef.current) {
          hasCompletedRef.current = true;
          if (pollingIntervalRef.current) {
            clearInterval(pollingIntervalRef.current);
            pollingIntervalRef.current = null;
          }
          if (onComplete && jobData.companyId && jobData.reportId) {
            onComplete(jobData.companyId, jobData.reportId);
          }
        }

        // Handle failure
        if (jobData.status === 'failed') {
          if (pollingIntervalRef.current) {
            clearInterval(pollingIntervalRef.current);
            pollingIntervalRef.current = null;
          }
          const errorMsg = jobData.error || 'PDF extraction failed';
          setError(errorMsg);
          if (onError) {
            onError(errorMsg);
          }
        }
      } catch (err) {
        console.error('Error polling job status:', err);
        const errorMsg = err instanceof Error ? err.message : 'Failed to check upload status';
        setError(errorMsg);
        if (pollingIntervalRef.current) {
          clearInterval(pollingIntervalRef.current);
          pollingIntervalRef.current = null;
        }
      }
    };

    // Initial poll
    pollStatus();

    // Set up interval for polling every 3 seconds (reduced from 1.5s to minimize server load)
    pollingIntervalRef.current = setInterval(pollStatus, 3000);

    // Cleanup on unmount
    return () => {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
      }
    };
  }, [uploadId, onComplete, onError]);

  // Stop polling when job reaches terminal state
  useEffect(() => {
    if (job && (job.status === 'completed' || job.status === 'failed')) {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
      }
    }
  }, [job?.status]);

  if (!job && !error) {
    return (
      <div className="border border-neutral-200 rounded-lg p-4 bg-white">
        <div className="flex items-center space-x-3">
          <Spinner size="sm" />
          <span className="text-sm text-neutral-600">Initializing PDF extraction...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="border border-red-300 rounded-lg p-4 bg-red-50">
        <div className="space-y-2">
          <div className="flex items-center space-x-2">
            <svg className="w-5 h-5 text-red-600" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
            </svg>
            <span className="text-sm font-semibold text-red-800">Upload Failed</span>
          </div>
          <p className="text-sm text-red-700 ml-7">{error}</p>
          <p className="text-xs text-neutral-600 ml-7">File: {filename}</p>
        </div>
      </div>
    );
  }

  if (!job) return null;

  const progressPercentage = job.progressPercentage ?? PHASE_PROGRESS[job.currentPhase] ?? 0;
  const isProcessing = job.status === 'pending' || job.status === 'processing';
  const isCompleted = job.status === 'completed';
  const isFailed = job.status === 'failed';

  // Determine status for each phase
  const getPhaseStatus = (phase: string): PhaseStatus => {
    const currentPhaseIndex = ALL_PHASES.indexOf(job.currentPhase as any);
    const phaseIndex = ALL_PHASES.indexOf(phase as any);

    if (isFailed && phaseIndex === currentPhaseIndex) {
      return 'failed';
    }
    if (phaseIndex < currentPhaseIndex) {
      return 'completed';
    }
    if (phaseIndex === currentPhaseIndex && isProcessing) {
      return 'in-progress';
    }
    if (phaseIndex === currentPhaseIndex && isCompleted) {
      return 'completed';
    }
    return 'pending';
  };

  return (
    <div className={`border rounded-lg p-4 ${
      isCompleted ? 'border-green-300 bg-green-50' :
      isFailed ? 'border-red-300 bg-red-50' :
      'border-primary-300 bg-primary-50'
    }`}>
      <div className="space-y-4">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-2">
            {isProcessing && (
              <Spinner size="sm" />
            )}
            {isCompleted && (
              <svg className="w-5 h-5 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
            )}
            {isFailed && (
              <svg className="w-5 h-5 text-red-600" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
            )}
            <span className={`text-sm font-semibold ${
              isCompleted ? 'text-green-800' :
              isFailed ? 'text-red-800' :
              'text-primary-700'
            }`}>
              {isCompleted ? 'Extraction Complete' :
               isFailed ? 'Extraction Failed' :
               'Processing PDF'}
            </span>
          </div>
          <span className="text-xs text-neutral-600">{filename}</span>
        </div>

        {/* Phase Stepper */}
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            {ALL_PHASES.map((phase, index) => {
              const status = getPhaseStatus(phase);
              const isLast = index === ALL_PHASES.length - 1;

              return (
                <div key={phase} className="flex items-center flex-1">
                  <div className="flex flex-col items-center">
                    {/* Phase Icon */}
                    <div className="relative flex items-center justify-center">
                      {status === 'completed' && (
                        <div className="w-8 h-8 rounded-full bg-green-500 flex items-center justify-center">
                          <svg className="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                          </svg>
                        </div>
                      )}
                      {status === 'in-progress' && (
                        <div className="w-8 h-8 rounded-full bg-primary-500 flex items-center justify-center">
                          <Spinner size="sm" />
                        </div>
                      )}
                      {status === 'failed' && (
                        <div className="w-8 h-8 rounded-full bg-red-500 flex items-center justify-center">
                          <svg className="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                          </svg>
                        </div>
                      )}
                      {status === 'pending' && (
                        <div className="w-8 h-8 rounded-full border-2 border-neutral-300 bg-white" />
                      )}
                    </div>
                    {/* Phase Label */}
                    <span className={`text-xs mt-1 text-center ${
                      status === 'completed' ? 'text-green-700 font-semibold' :
                      status === 'in-progress' ? 'text-primary-700 font-semibold' :
                      status === 'failed' ? 'text-red-700 font-semibold' :
                      'text-neutral-500'
                    }`}>
                      {PHASE_LABELS[phase] || phase}
                    </span>
                  </div>
                  {/* Connecting Line */}
                  {!isLast && (
                    <div className={`flex-1 h-0.5 mx-2 ${
                      status === 'completed' ? 'bg-green-500' :
                      status === 'in-progress' ? 'bg-primary-300' :
                      'bg-neutral-300'
                    }`} />
                  )}
                </div>
              );
            })}
          </div>
          {/* Overall Progress Percentage */}
          <div className="flex justify-end">
            <span className={`text-xs font-semibold ${
              isCompleted ? 'text-green-700' :
              isFailed ? 'text-red-700' :
              'text-primary-700'
            }`}>
              {progressPercentage}% Complete
            </span>
          </div>
        </div>

        {/* Status Message */}
        {job.progressMessage && (
          <p className="text-xs text-neutral-700 italic">{job.progressMessage}</p>
        )}

        {/* Error Message */}
        {job.error && (
          <div className="text-xs text-red-700 bg-red-100 rounded p-2">
            {job.error}
          </div>
        )}

        {/* Success Details */}
        {isCompleted && job.companyId && (
          <div className="text-xs space-y-1 bg-green-100 rounded p-2">
            <div className="flex justify-between">
              <span className="text-neutral-700">Company ID:</span>
              <span className="text-green-800 font-mono font-semibold">{job.companyId}</span>
            </div>
            {job.reportId && (
              <div className="flex justify-between">
                <span className="text-neutral-700">Report ID:</span>
                <span className="text-green-800 font-mono font-semibold">{job.reportId}</span>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
